/* SCQ order page — content-section custom elements.
   <scq-timeline>      scroll-driven progress fill + active dots (What to Expect)
   <scq-before-after>  Swiper init for the before/after slider (What to Expect)
   <scq-faq>           single-open fallback for <details name> accordions (FAQ)
   Ported from kk-headless scq/quiz/order4/page.tsx. No dependencies besides window.Swiper. */

(function () {
  'use strict';

  /* ---------- <scq-timeline> ---------- */
  if (!customElements.get('scq-timeline')) {
    class ScqTimeline extends HTMLElement {
      connectedCallback() {
        this.fill = this.querySelector('[data-scq-timeline-fill]');
        this.dots = Array.from(this.querySelectorAll('[data-scq-timeline-dot]'));
        if (!this.fill || !this.dots.length) return;

        this.ticking = false;
        this.onScroll = this.onScroll.bind(this);
        this.update = this.update.bind(this);

        // Only listen to scroll while the timeline is near the viewport.
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => (entry.isIntersecting ? this.listen() : this.unlisten()));
            },
            { rootMargin: '25% 0px 25% 0px' }
          );
          this.observer.observe(this);
        } else {
          this.listen();
        }
        this.update();
      }

      disconnectedCallback() {
        this.unlisten();
        if (this.observer) this.observer.disconnect();
      }

      listen() {
        if (this.listening) return;
        this.listening = true;
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.update();
      }

      unlisten() {
        if (!this.listening) return;
        this.listening = false;
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(this.update);
      }

      update() {
        this.ticking = false;
        const rect = this.getBoundingClientRect();
        const lastDot = this.dots[this.dots.length - 1];
        const last = lastDot.getBoundingClientRect().top - rect.top;
        const threshold = window.innerHeight * 0.8;
        let height = rect.top < threshold ? threshold - rect.top : 0;
        height = Math.max(0, Math.min(height, last));
        this.fill.style.height = `${height}px`;
        this.dots.forEach((dot) => {
          const pos = dot.getBoundingClientRect().top - rect.top;
          dot.classList.toggle('is-active', height >= pos - 7);
        });
      }
    }
    customElements.define('scq-timeline', ScqTimeline);
  }

  /* ---------- <scq-before-after> ---------- */
  if (!customElements.get('scq-before-after')) {
    class ScqBeforeAfter extends HTMLElement {
      connectedCallback() {
        this.attempts = 0;
        this.init();
      }

      disconnectedCallback() {
        if (this.retry) clearTimeout(this.retry);
        if (this.swiper && typeof this.swiper.destroy === 'function') this.swiper.destroy(true, true);
      }

      init() {
        if (this.swiper) return;
        if (typeof window.Swiper !== 'function') {
          // swiper.min.js is deferred alongside this file; wait for it (up to ~5s).
          if (this.attempts++ < 50) this.retry = setTimeout(() => this.init(), 100);
          return;
        }
        const pagination = this.querySelector('.swiper-pagination');
        this.swiper = new window.Swiper(this, {
          spaceBetween: 15,
          slidesPerView: 1.3,
          loop: true,
          pagination: pagination ? { el: pagination, clickable: true } : undefined,
          breakpoints: {
            0: { slidesPerView: 1.3, spaceBetween: 15, allowTouchMove: true },
            768: { slidesPerView: 3, spaceBetween: 15, allowTouchMove: false },
          },
        });
      }
    }
    customElements.define('scq-before-after', ScqBeforeAfter);
  }

  /* ---------- <scq-faq> ---------- */
  if (!customElements.get('scq-faq')) {
    class ScqFaq extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('details'));
        if (!this.items.length) return;
        // Browsers supporting <details name> enforce exclusivity natively.
        if ('name' in document.createElement('details')) return;
        this.onToggle = this.onToggle.bind(this);
        this.items.forEach((item) => item.addEventListener('toggle', this.onToggle));
      }

      disconnectedCallback() {
        if (!this.onToggle) return;
        this.items.forEach((item) => item.removeEventListener('toggle', this.onToggle));
      }

      onToggle(event) {
        const opened = event.currentTarget;
        if (!opened.open) return;
        this.items.forEach((item) => {
          if (item !== opened && item.open) item.open = false;
        });
      }
    }
    customElements.define('scq-faq', ScqFaq);
  }
})();
